import { Box, Stack } from '@mui/joy';
import { ArrowClockwise } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from 'chatbot/components';
import { useLlmActions, useQuoteActions } from 'chatbot/hooks';
import {
  ChatbotConversationDirection,
  ChatbotConversationType,
  IChatbotConversation,
  IChatbotPart,
  IChatbotQuestion,
} from 'chatbot/types';
import { Loader } from 'core/elements';

import { ConversationItem } from './ConversationItem';
import { ConversationMessage } from './ConversationMessage';
import { chatbotResponseToConversationMap } from './utils';

interface IProps {
  quoteId: number;
  threadId: string;
  initialMessage: string;
  onReset: () => void;
  onComplete?: () => void;
}

export const Chat = ({ quoteId, threadId, initialMessage, onReset, onComplete }: IProps) => {
  const [conversation, setConversation] = useState<IChatbotConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const conversationContainerRef = useRef<HTMLDivElement>(null);
  const conversationStackRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  const { sendMessage, sendAnswers } = useLlmActions();
  const { createCustomerQuoteParts } = useQuoteActions();

  const handleSendMessage = useCallback(
    async (message: string, addOutgoingMessageToConversation = true) => {
      if (addOutgoingMessageToConversation) {
        setConversation((prev) => [
          ...prev,
          {
            type: ChatbotConversationType.MESSAGE,
            direction: ChatbotConversationDirection.OUTGOING,
            message,
          },
        ]);
      }

      setIsLoading(true);

      const response = await sendMessage({
        threadId,
        message,
      });

      setIsLoading(false);
      setConversation((prev) => [...prev, chatbotResponseToConversationMap(response)]);
    },
    [sendMessage, threadId]
  );

  const handleSendAnswers = useCallback(
    async (answers: IChatbotQuestion[]) => {
      setIsLoading(true);

      const response = await sendAnswers({
        threadId,
        answers,
      });

      setIsLoading(false);
      setConversation((prev) => [...prev, chatbotResponseToConversationMap(response)]);
    },
    [sendAnswers, threadId]
  );

  const handleUseThisBom = useCallback(
    async (parts: IChatbotPart[]) => {
      setIsLoading(true);

      try {
        await createCustomerQuoteParts({ quoteId, items: parts });

        onComplete?.();
      } catch (error) {
        // Error is already handled by useHandleErrors hook in useQuoteActions
        // Just stop the loading state
      } finally {
        setIsLoading(false);
      }
    },
    [createCustomerQuoteParts, quoteId, onComplete]
  );

  useEffect(() => {
    if (threadId && initialMessage) {
      setConversation([]);
      handleSendMessage(initialMessage, false);
    }
  }, [threadId, initialMessage, handleSendMessage]);

  useEffect(() => {
    setTimeout(() => {
      if (isLoading) {
        conversationContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else {
        conversationStackRef.current?.lastElementChild?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 100);
  }, [conversation, isLoading]);

  const conversationWithCallbacks = useMemo(() => {
    const clonedConversation = conversation.map((item) => ({ ...item }));
    const lastItem = clonedConversation.at(-1);

    if (!isLoading && lastItem?.type === ChatbotConversationType.QUESTIONS) {
      lastItem.onSubmit = handleSendAnswers;
    }

    if (!isLoading && lastItem?.type === ChatbotConversationType.PARTS_LIST) {
      lastItem.onUseThisBom = handleUseThisBom;
    }

    return clonedConversation;
  }, [conversation, handleSendAnswers, handleUseThisBom, isLoading]);

  const shouldDisplayAskAIInput = useMemo(() => {
    const lastItem = conversationWithCallbacks.at(-1);

    return lastItem?.type !== ChatbotConversationType.QUESTIONS && !isLoading;
  }, [conversationWithCallbacks, isLoading]);

  if (!initialMessage) {
    return null;
  }

  return (
    <Box ref={conversationContainerRef} sx={{ py: 2 }}>
      <Stack sx={{ mb: 2, gap: 2 }} ref={conversationStackRef}>
        <ConversationMessage
          message={initialMessage}
          buttons={[
            {
              startDecorator: <ArrowClockwise />,
              text: t('buttonLabelRegenerate'),
              onClick: onReset,
            },
          ]}
        />

        {conversationWithCallbacks.map((item, index) => (
          <ConversationItem key={index} item={item} />
        ))}
      </Stack>

      {isLoading && <Loader style={{ height: 200 }} />}

      {shouldDisplayAskAIInput && <Input onSubmit={handleSendMessage} />}
    </Box>
  );
};
